import React, { useState, useCallback } from "react";
import ScoreBadge from "./ScoreBadge";
import InstrumentBadge from "./InstrumentBadge";
import CategoryTag from "./CategoryTag";
import DeepAnalysisPanel from "./DeepAnalysisPanel";
import { Clock, User, Twitter, PenLine, Zap, ChevronDown, ChevronUp } from "lucide-react";

function getPostedTime(dateStr) {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  // Convert to Eastern Time
  const et = new Date(date.toLocaleString('en-US', { timeZone: 'America/New_York' }));
  const now = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' }));
  const isToday = et.toDateString() === now.toDateString();
  const timeStr = date.toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true });
  if (isToday) {
    return timeStr + ' ET';
  }
  return date.toLocaleDateString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric' }) + ' ' +
    date.toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit', hour12: true }) + ' ET';
}

function PlatformBadge({ platform }) {
  if (platform === "twitter") {
    return (
      <span
        className="inline-flex items-center gap-1 font-mono text-[10px] px-1.5 py-0.5 rounded"
        style={{
          backgroundColor: 'rgba(29, 155, 240, 0.12)',
          color: '#1d9bf0',
          border: '1px solid rgba(29, 155, 240, 0.25)',
        }}
      >
        <Twitter size={9} />
        X
      </span>
    );
  }
  if (platform === "manual") {
    return (
      <span
        className="inline-flex items-center gap-1 font-mono text-[10px] px-1.5 py-0.5 rounded"
        style={{
          backgroundColor: 'rgba(148, 163, 184, 0.1)',
          color: '#8b949e',
          border: '1px solid rgba(148, 163, 184, 0.2)',
        }}
      >
        <PenLine size={9} />
        MANUAL
      </span>
    );
  }
  if (platform === "simulation") {
    return (
      <span
        className="inline-flex items-center gap-1 font-mono text-[10px] px-1.5 py-0.5 rounded"
        style={{
          backgroundColor: 'rgba(245, 158, 11, 0.1)',
          color: '#f59e0b',
          border: '1px solid rgba(245, 158, 11, 0.2)',
        }}
      >
        SIM
      </span>
    );
  }
  if (platform === "digest") {
    return (
      <span
        className="inline-flex items-center gap-1 font-mono text-[10px] px-1.5 py-0.5 rounded"
        style={{
          backgroundColor: 'rgba(168, 85, 247, 0.12)',
          color: '#a855f7',
          border: '1px solid rgba(168, 85, 247, 0.25)',
        }}
      >
        <Zap size={9} />
        DIGEST
      </span>
    );
  }
  return null;
}

const FeedCard = React.memo(function FeedCard({ post, isExpanded, onToggleExpand, filters, onToggleFilter }) {
  const { source, content, analysis, timestamps, has_deep_analysis } = post;
  const [postedTime] = useState(() => getPostedTime(timestamps?.posted_at));
  const [hasDeep, setHasDeep] = useState(has_deep_analysis || false);

  const handleScoreClick = useCallback(() => {
    onToggleExpand(post.post_id);
  }, [onToggleExpand, post.post_id]);

  const handleDeepClose = useCallback(() => {
    setHasDeep(true);
    onToggleExpand(null);
  }, [onToggleExpand]);

  if (!analysis) return null;

  const isDigest = content?.is_digest === true;
  const tweetUrl = content?.url || "";
  const handle = source?.handle || "";
  const handleClean = handle.replace("@", "");
  const profileUrl = handleClean ? `https://x.com/${handleClean}` : "";

  // ─── Digest Card Rendering ──────────────────────────────────────────
  if (isDigest) {
    return <DigestCard post={post} isExpanded={isExpanded} onToggleExpand={onToggleExpand} filters={filters} onToggleFilter={onToggleFilter} />;
  }

  return (
    <div
      data-testid="feed-card"
      className="cw-card-enter rounded-[10px] border overflow-hidden transition-colors duration-150"
      style={{
        backgroundColor: '#161b22',
        borderColor: isExpanded ? '#f59e0b40' : '#30363d',
      }}
      onMouseEnter={(e) => {
        if (!isExpanded) e.currentTarget.style.borderColor = '#2a4a35';
      }}
      onMouseLeave={(e) => {
        if (!isExpanded) e.currentTarget.style.borderColor = '#30363d';
      }}
    >
      <div className="p-3 sm:p-4">
        {/* Top row */}
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="flex items-center gap-2 min-w-0 flex-wrap">
            <div
              className="hidden sm:flex items-center justify-center rounded-full shrink-0"
              style={{
                width: 28,
                height: 28,
                backgroundColor: '#21262d',
                border: '1px solid #30363d',
              }}
            >
              <User size={14} style={{ color: '#8b949e' }} />
            </div>
            <div className="flex items-center gap-1.5 min-w-0 flex-wrap">
              <a
                href={profileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="source-link inline-flex items-center gap-1 sm:gap-1.5 min-w-0"
                style={{ color: 'inherit', textDecoration: 'none' }}
                data-testid="feed-card-source-link"
                onClick={(e) => {
                  if (e.shiftKey && onToggleFilter) {
                    e.preventDefault();
                    e.stopPropagation();
                    onToggleFilter('sources', handle);
                  }
                }}
              >
                <span
                  className="display-name font-mono text-xs font-medium truncate"
                  style={{ color: filters?.sources?.includes(handle) ? '#4ade80' : '#e6edf3' }}
                >
                  {source?.display_name || 'unknown'}
                </span>
                <span
                  className="hidden sm:inline font-mono text-xs"
                  style={{ color: filters?.sources?.includes(handle) ? '#4ade80' : '#484f58' }}
                >
                  {handle}
                </span>
              </a>
              <PlatformBadge platform={source?.platform} />
              <span className="flex items-center gap-1">
                <Clock size={10} style={{ color: '#484f58' }} />
                {tweetUrl ? (
                  <a
                    href={tweetUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-mono text-[10px] hover:underline"
                    style={{ color: '#8b949e', textDecoration: 'none' }}
                    data-testid="feed-card-timestamp-link"
                  >
                    {postedTime}
                  </a>
                ) : (
                  <span className="font-mono text-[10px]" style={{ color: '#484f58' }}>
                    {postedTime}
                  </span>
                )}
              </span>
            </div>
          </div>
          {/* Clickable score badge */}
          <div
            data-testid="feed-card-score-badge-wrapper"
            onClick={handleScoreClick}
            className="cursor-pointer relative shrink-0"
            title="Click for deep analysis"
          >
            <ScoreBadge
              score={analysis.overall_score}
              label={analysis.overall_label}
              isExpanded={isExpanded}
            />
            {hasDeep && !isExpanded && (
              <span
                className="absolute -top-0.5 -right-0.5 rounded-full"
                style={{
                  width: 6,
                  height: 6,
                  backgroundColor: '#f59e0b',
                  border: '1px solid #161b22',
                }}
                title="Deep analysis available (cached)"
              />
            )}
          </div>
        </div>

        {/* Headline */}
        {tweetUrl ? (
          <a
            href={tweetUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="feed-card-headline"
            className="font-mono text-sm sm:text-[15px] font-medium leading-snug mb-3 block headline-link"
            style={{ color: '#e6edf3', textDecoration: 'none' }}
          >
            {content?.text || ''}
          </a>
        ) : (
          <p
            data-testid="feed-card-headline"
            className="font-mono text-sm sm:text-[15px] font-medium leading-snug mb-3"
            style={{ color: '#e6edf3' }}
          >
            {content?.text || ''}
          </p>
        )}

        {/* Instrument badges */}
        {analysis.instruments?.length > 0 && (
          <div data-testid="feed-card-instrument-badges" className="flex flex-wrap gap-1.5 mb-3">
            {analysis.instruments.map((inst, i) => (
              <InstrumentBadge
                key={`${inst.symbol}-${i}`}
                instrument={inst}
                onClick={onToggleFilter ? () => onToggleFilter('instruments', inst.symbol) : undefined}
                isActive={filters?.instruments?.includes(inst.symbol)}
              />
            ))}
          </div>
        )}

        {/* Summary */}
        <p
          className="font-sans text-xs sm:text-sm leading-5 sm:leading-6 mb-3"
          style={{ color: '#c9d1d9', fontStyle: 'italic' }}
        >
          {analysis.summary}
        </p>

        {/* Tags + analysis metadata */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div data-testid="feed-card-category-tags" className="flex flex-wrap gap-1.5">
            {analysis.tags?.map((tag, i) => (
              <CategoryTag
                key={`${tag}-${i}`}
                tag={tag}
                onClick={onToggleFilter ? () => onToggleFilter('tags', tag) : undefined}
                isActive={filters?.tags?.includes(tag)}
              />
            ))}
          </div>
          <span className="font-mono text-[10px] sm:text-[11px] shrink-0" style={{ color: '#8b949e' }}>
            {analysis.latency_ms}ms &middot; MacroFlare AI
          </span>
        </div>
      </div>

      {/* Deep Analysis Panel — expanded */}
      {isExpanded && (
        <DeepAnalysisPanel
          postId={post.post_id}
          onClose={handleDeepClose}
        />
      )}
    </div>
  );
});

export default FeedCard;


// ─── Digest Card Sub-Component ──────────────────────────────────────────

function DigestCard({ post }) {
  const [showPosts, setShowPosts] = useState(false);
  const { content, analysis, timestamps } = post;
  const digestCount = content?.digest_count || 0;
  const digestSources = content?.digest_sources || [];
  const digestPosts = content?.digest_posts || [];
  const keyEvents = content?.key_events || [];
  const timeRange = content?.digest_time_range || {};

  const postedTime = getPostedTime(timestamps?.posted_at);

  return (
    <div
      data-testid="digest-card"
      className="cw-card-enter rounded-[10px] border overflow-hidden"
      style={{
        backgroundColor: '#161b22',
        borderColor: 'rgba(168, 85, 247, 0.25)',
        borderLeft: '3px solid #a855f7',
      }}
    >
      <div className="p-3 sm:p-4">
        {/* Header */}
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center justify-center rounded-full shrink-0"
              style={{ width: 28, height: 28, backgroundColor: 'rgba(168,85,247,0.12)', border: '1px solid rgba(168,85,247,0.25)' }}>
              <Zap size={14} style={{ color: '#a855f7' }} />
            </div>
            <span className="font-mono text-xs font-bold" style={{ color: '#a855f7' }}>MACRO DIGEST</span>
            <PlatformBadge platform="digest" />
            <span className="font-mono text-[10px]" style={{ color: '#484f58' }}>
              {digestCount} posts from {digestSources.length} sources
            </span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <ScoreBadge score={analysis?.overall_score} label={analysis?.overall_label} />
            <span className="font-mono text-[10px]" style={{ color: '#484f58' }}>{postedTime}</span>
          </div>
        </div>

        {/* Headline */}
        <p className="font-mono text-sm font-bold mb-2" style={{ color: '#e6edf3' }}>
          {content?.text}
        </p>

        {/* Instruments */}
        {analysis?.instruments?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-2">
            {analysis.instruments.map((inst, i) => (
              <InstrumentBadge key={`digest-inst-${inst.symbol}-${i}`} instrument={inst} />
            ))}
          </div>
        )}

        {/* Key Events */}
        {keyEvents.length > 0 && (
          <div className="mb-2 space-y-1.5">
            {keyEvents.map((ev, i) => (
              <div key={`ke-${ev.theme}-${i}`} className="p-2 rounded" style={{ backgroundColor: '#0d1117', border: '1px solid #21262d' }}>
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="font-mono text-[10px] font-bold" style={{ color: '#e6edf3' }}>{ev.theme}</span>
                  <span className="font-mono text-[9px]" style={{ color: '#484f58' }}>
                    {ev.sources?.join(', ')}
                  </span>
                </div>
                <p className="font-mono text-[11px]" style={{ color: '#8b949e' }}>{ev.summary}</p>
                {ev.instruments?.length > 0 && (
                  <div className="flex gap-1 mt-1">
                    {ev.instruments.map((inst, j) => (
                      <span key={`kei-${inst.symbol}-${j}`} className="font-mono text-[9px] px-1 py-0.5 rounded"
                        style={{
                          color: inst.score > 0 ? '#22c55e' : inst.score < 0 ? '#ef4444' : '#8b949e',
                          backgroundColor: inst.score > 0 ? 'rgba(34,197,94,0.08)' : inst.score < 0 ? 'rgba(239,68,68,0.08)' : 'rgba(139,148,158,0.08)',
                        }}>
                        {inst.symbol} {inst.score > 0 ? '+' : ''}{inst.score}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Summary */}
        <p className="font-mono text-xs leading-relaxed mb-2" style={{ color: '#8b949e' }}>
          {analysis?.summary}
        </p>

        {/* Tags */}
        {analysis?.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-2">
            {analysis.tags.map((tag, i) => (
              <CategoryTag key={`dt-${tag}-${i}`} tag={tag} />
            ))}
          </div>
        )}

        {/* Expandable individual posts */}
        <button
          data-testid="digest-expand-posts"
          onClick={() => setShowPosts(!showPosts)}
          className="flex items-center gap-1 font-mono text-[10px] transition-colors"
          style={{ color: '#484f58' }}
        >
          {showPosts ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
          {showPosts ? 'HIDE' : 'SHOW'} {digestPosts.length} INDIVIDUAL POSTS
        </button>

        {showPosts && digestPosts.length > 0 && (
          <div className="mt-2 space-y-1 max-h-48 overflow-y-auto">
            {digestPosts.map((dp, i) => (
              <div key={dp.post_id || `dp-${i}`} className="flex items-start gap-2 p-1.5 rounded" style={{ backgroundColor: '#0d1117' }}>
                <span className="font-mono text-[10px] font-bold shrink-0" style={{ color: '#8b949e' }}>@{dp.source}</span>
                <span className="font-mono text-[10px]" style={{ color: '#484f58' }}>{dp.text}</span>
              </div>
            ))}
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center gap-3 mt-2 pt-2 border-t" style={{ borderColor: '#21262d' }}>
          <span className="font-mono text-[9px]" style={{ color: '#484f58' }}>
            {analysis?.latency_ms}ms · MacroFlare AI
          </span>
          {timeRange.start && timeRange.end && (
            <span className="font-mono text-[9px]" style={{ color: '#484f58' }}>
              Window: {getPostedTime(timeRange.start)} – {getPostedTime(timeRange.end)}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
